<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';

import { confirmAction } from '@/services/confirm';

/**
 * 右侧抽屉的唯一实现。
 *
 * 之前 BOM 挂接、变更关联、计划节点编辑各自维护一份遮罩+侧板+动画的 CSS，
 * 调一次规范要改四处，且各自都忘了 Esc。收敛到这里之后：
 * Esc / 点遮罩 / 关闭按钮走同一条 requestClose，`dirty` 为真时先问一句再关，
 * 填了一半的表单不会因为误点遮罩直接丢掉。
 */
const props = withDefaults(defineProps<{
  title: string;
  /** 标题上方的小字眉标，如 NODE / EDIT */
  kicker?: string;
  width?: string;
  /** 有未保存内容时，关闭前要求确认 */
  dirty?: boolean;
}>(), { kicker: '', width: 'min(760px, 92vw)', dirty: false });

const emit = defineEmits<{ (event: 'close'): void }>();

async function requestClose(): Promise<void> {
  if (props.dirty) {
    const confirmed = await confirmAction({
      title: '有未保存的修改，确认关闭？',
      message: '关闭后本次填写的内容会丢失。',
      danger: true,
      confirmLabel: '关闭不保存',
    });
    if (!confirmed) return;
  }
  emit('close');
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') void requestClose();
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

defineExpose({ requestClose });
</script>

<template>
  <div class="drawer-backdrop" @click.self="requestClose">
    <aside class="drawer" role="dialog" aria-modal="true" :aria-label="title" :style="{ width }">
      <header class="drawer-header">
        <div>
          <span v-if="kicker" class="drawer-kicker">{{ kicker }}</span>
          <h2>{{ title }}</h2>
          <slot name="subtitle" />
        </div>
        <button type="button" class="drawer-close" aria-label="关闭" @click="requestClose">×</button>
      </header>
      <slot />
      <footer v-if="$slots.footer" class="drawer-footer">
        <slot name="footer" />
      </footer>
    </aside>
  </div>
</template>

<style scoped>
/* 遮罩淡入用 ::before，避免父级 opacity 把侧板一起淡掉（只动 transform/opacity） */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  justify-content: flex-end;
  background: transparent;
}
.drawer-backdrop::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(11, 25, 44, .38);
  animation: drawer-backdrop-in var(--duration-overlay, 220ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));
  pointer-events: none;
}

.drawer {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  color: var(--ink);
  background: var(--white);
  box-shadow: -18px 0 46px rgba(11, 25, 44, .18);
  animation: drawer-in var(--duration-overlay, 220ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));
}

@keyframes drawer-backdrop-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes drawer-in {
  from { transform: translateX(16px); opacity: 0; }
  to { transform: none; opacity: 1; }
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 22px 12px;
  border-bottom: 1px solid var(--line);
}

.drawer-kicker {
  color: var(--blue);
  font: 800 12px var(--display);
  letter-spacing: .12em;
}

.drawer-header h2 {
  margin: 2px 0 4px;
  font-size: 17px;
  line-height: 1.12;
}

.drawer-header :deep(p) {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.7;
}

.drawer-close {
  border: 0;
  background: transparent;
  color: var(--muted);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: transform var(--duration-press, 160ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)), color .15s var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));
}
.drawer-close:active { transform: scale(0.97); }
@media (hover: hover) and (pointer: fine) {
  .drawer-close:hover { color: var(--ink); }
}

.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
  padding: 14px 22px;
  border-top: 1px solid var(--line);
  background: var(--paper);
}
</style>
